import * as React from "react";
import { useSnackbar } from "notistack";
import { ChevronLeft, ChevronRight } from "lucide-react";

type PageControlProps = {
  currentPage: number;
  isLoading?: boolean;
  onNext: () => any;
  onPrev: () => any;
  pageSize?: number;
  setPageSize?: (pageSize: number) => void;
  disableNext?: boolean;
  disablePrev?: boolean;
  enqueueSnackbar?: (node: React.ReactNode, options?: any) => void; // injected by withSnackbar
};
class PageControlBase extends React.Component<PageControlProps, {}> {
  onNext = async (e: any) => {
    const { isLoading } = this.props;
    e.preventDefault();
    if (this.props.disableNext || isLoading) return;
    try {
      await this.props.onNext();
    } catch (e) {
      const errMsg = getErrorMessage(e, "Error fetching next page");
      this.props.enqueueSnackbar &&
        this.props.enqueueSnackbar(
          <span style={{ fontSize: 12 }}>{errMsg}</span>,
          { variant: "error" },
        );
    }
  };

  onPrev = async (e: any) => {
    const { isLoading } = this.props;
    e.preventDefault();
    if (this.props.disablePrev || isLoading) return;
    try {
      await this.props.onPrev();
    } catch (e) {
      const errMsg = getErrorMessage(e, "Error fetching previous page");
      this.props.enqueueSnackbar &&
        this.props.enqueueSnackbar(
          <span style={{ fontSize: 12 }}>{errMsg}</span>,
          { variant: "error" },
        );
    }
  };

  onSelectPageSize = (e: React.ChangeEvent) => {
    const { value } = e.target as HTMLSelectElement;
    const pageSize = parseInt(value, 10);
    if (this.props.setPageSize) {
      this.props.setPageSize(pageSize);
    }
  };

  public render() {
    const { currentPage, disableNext, disablePrev, isLoading } = this.props;

    return (
      <div className="relative flex flex-col items-center gap-3 px-4 py-3 border-t border-slate-200 sm:min-h-16 sm:justify-center">
        {this.props.setPageSize ? (
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-slate-500 sm:absolute sm:left-4 sm:top-1/2 sm:-translate-y-1/2 sm:justify-start">
            <label className="inline-flex items-center gap-2">
              <span>Rows per page</span>
              <select
                aria-label="Rows per page"
                className="h-8 px-2 text-sm bg-white border rounded-md outline-none border-slate-200 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                value={this.props.pageSize?.toString()}
                onChange={this.onSelectPageSize}
              >
                <option value="10">10</option>
                <option value="30">30</option>
                <option value="50">50</option>
              </select>
            </label>
          </div>
        ) : null}

        <nav aria-label="Table pagination" className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 px-3 text-sm font-medium transition-colors bg-white border rounded-md h-9 border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            disabled={disablePrev || isLoading}
            onClick={this.onPrev}
          >
            <ChevronLeft className="size-4" />
            Previous
          </button>
          <span
            aria-current="page"
            className="inline-flex items-center justify-center px-3 text-sm font-semibold rounded-md bg-slate-100 h-9 min-w-9 text-slate-900"
          >
            {String(currentPage + 1).padStart(2, "0")}
          </span>
          <button
            type="button"
            className="inline-flex items-center gap-1 px-3 text-sm font-semibold transition-colors bg-blue-400 border border-transparent rounded-md h-9 text-slate-900 hover:bg-blue-300 disabled:cursor-not-allowed disabled:opacity-40"
            disabled={disableNext || isLoading}
            onClick={this.onNext}
          >
            Next
            <ChevronRight className="size-4" />
          </button>
        </nav>
      </div>
    );
  }
}

function getErrorMessage(e: any, fallback: string) {
  try {
    if (e?.response) {
      const data =
        typeof e.response === "string" ? JSON.parse(e.response) : e.response;
      if (data?.errors?.length)
        return data.errors.map((x: any) => x.message).join(", ");
      if (data?.message) return data.message;
    }
    if (e?.message) return e.message;
  } catch {
    // swallow parse / structure errors and use fallback
  }
  return fallback;
}

// Wrapper component injecting snackbar via hook (replacing deprecated withSnackbar)
export const PageControl: React.FC<
  Omit<PageControlProps, "enqueueSnackbar">
> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  return <PageControlBase {...props} enqueueSnackbar={enqueueSnackbar} />;
};
